/**
 * @file TaskCategoriesTab.tsx
 * @brief Manager settings tab for maintaining task category enum_master values.
 * @author Nestbotics
 * @date 2026-06-19
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Plus, Trash2, Tag } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  enumMasterService,
  TASK_CATEGORY_KEY,
  TASK_CATEGORY_MODULE,
  type EnumMasterItem,
} from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

/**
 * TaskCategoriesTab allows managers to add or deactivate task categories stored in enum_master.
 */
const TaskCategoriesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [newLabel, setNewLabel] = useState('');
  const [confirmItem, setConfirmItem] = useState<EnumMasterItem | null>(null);

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: QUERY_KEYS.taskCategories(true),
    queryFn: () => enumMasterService.listTaskCategories(true),
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: (displayLabel: string) =>
      enumMasterService.create({
        moduleName: TASK_CATEGORY_MODULE,
        keyName: TASK_CATEGORY_KEY,
        displayLabel,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', TASK_CATEGORY_MODULE, TASK_CATEGORY_KEY] });
      setNewLabel('');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (enumId: string) => enumMasterService.deactivate(enumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', TASK_CATEGORY_MODULE, TASK_CATEGORY_KEY] });
      setConfirmItem(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (enumId: string) => enumMasterService.update(enumId, { isActive: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', TASK_CATEGORY_MODULE, TASK_CATEGORY_KEY] });
    },
  });

  const activeCategories = categories.filter((item) => item.isActive);
  const inactiveCategories = categories.filter((item) => !item.isActive);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    createMutation.mutate(label);
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tag size={20} />
        <Typography variant="h6" fontWeight={600}>
          Task Categories
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Categories are stored in enum_master and appear in the Create Task dropdown for all users.
        Managers can add new categories here.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Add Category
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Category name"
              placeholder="e.g. QA Review"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleAdd}
              disabled={!newLabel.trim() || createMutation.isPending}
            >
              Add
            </Button>
          </Box>
          {createMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(createMutation.error as Error).message || 'Failed to add category'}
            </Alert>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">Failed to load task categories.</Alert>
      ) : (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Active Categories ({activeCategories.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {activeCategories.map((item) => (
              <Chip
                key={item.id}
                label={item.displayLabel}
                onDelete={() => setConfirmItem(item)}
                deleteIcon={<Trash2 size={14} />}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>

          {inactiveCategories.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Inactive ({inactiveCategories.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {inactiveCategories.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.displayLabel}
                    variant="outlined"
                    size="small"
                    onClick={() => reactivateMutation.mutate(item.id)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Click an inactive category to restore it.
              </Typography>
            </>
          )}
        </>
      )}

      <Dialog open={Boolean(confirmItem)} onClose={() => setConfirmItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Category</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Deactivate &quot;{confirmItem?.displayLabel}&quot;? Existing tasks keep their category value,
            but it will no longer appear in the dropdown.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmItem(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmItem && deactivateMutation.mutate(confirmItem.id)}
            disabled={deactivateMutation.isPending}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskCategoriesTab;
